import { useCallback } from 'react'

import { useForm, FieldValues, useWatch } from 'react-hook-form'

import { SplitsLink as Link } from '~/components/Link'
import { TextAreaInput } from '~/components/component-library'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { useRequiredActiveOrg } from '~/hooks/org'
import { useServerRequests } from '~/hooks/useRequests'

export const FeedbackDialog = ({
  closeDialog,
  defaultMessage,
}: {
  closeDialog: () => void
  defaultMessage?: string
}) => {
  const { control, handleSubmit } = useForm({
    defaultValues: {
      feedback: defaultMessage ?? '',
    },
  })
  const { serverPostRequest, isProcessing } = useServerRequests()
  const { id: orgId } = useRequiredActiveOrg()

  const onSubmit = useCallback(
    async (data: FieldValues) =>
      serverPostRequest({
        path: '/api/feedback',
        params: {
          feedbackText: data.feedback,
          orgId,
        },
        errorMessage: 'Failed to send feedback',
        successMessage: "Question sent — we'll be in touch soon",
      }).then((result) => {
        if (result?.data) {
          closeDialog()
        }
      }),
    [serverPostRequest, closeDialog, orgId],
  )

  const feedback = useWatch({ control, name: 'feedback' })
  const isDisabled = !feedback

  return (
    <SplitsDialogContent
      id="feedbackDialog"
      title="Ask a question"
      variant="cta"
      isLoading={isProcessing}
      actionButton={{
        text: 'Send question',
        onClick: handleSubmit(onSubmit),
        isDisabled: isDisabled,
      }}
      cancelButton={{
        text: 'Cancel',
        onClick: closeDialog,
      }}
    >
      <p>
        How can we help? Please share any relevant information we may need to
        answer your question.
      </p>
      <TextAreaInput
        control={control}
        inputName="feedback"
        id="feedback"
        placeholder="How do I..."
        rows={8}
        footer={
          <span>
            You can also email us at{' '}
            <Link path="mailto:support@splits.org" underline target="tab">
              support@splits.org
            </Link>
          </span>
        }
      />
    </SplitsDialogContent>
  )
}
